import { request } from '@umijs/max';
import { Button, Card, Input, Modal, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';

type ApiResponse<T> = {
  success: boolean;
  data: T;
};

type PageResponse<T> = {
  list: T[];
  current: number;
  pageSize: number;
  total: number;
};

export type FinanceLogRow = {
  id: number;
  bizOrderNo?: string;
  changeType: 'CONSUME' | 'CONSUME_AI_AGENT' | 'REFUND' | 'ADJUST';
  pointsChange: number;
  balanceBefore: number;
  balanceAfter: number;
  remark?: string;
  occurredAt?: string;
};

const typeOptions = [
  { label: '投放消耗', value: 'CONSUME' },
  { label: 'AI Agent 消耗', value: 'CONSUME_AI_AGENT' },
  { label: '退款', value: 'REFUND' },
  { label: '调整', value: 'ADJUST' },
];

const typeColor: Record<string, string> = {
  CONSUME: 'processing',
  CONSUME_AI_AGENT: 'purple',
  REFUND: 'success',
  ADJUST: 'default',
};

export const FinanceLogsTable: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [changeType, setChangeType] = useState<string>();
  const [rows, setRows] = useState<FinanceLogRow[]>([]);
  const [page, setPage] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchList = async () => {
    setLoading(true);
    try {
      const resp = await request<ApiResponse<PageResponse<FinanceLogRow>>>(
        '/api/geo/modules/delivery/finance-logs',
        {
          method: 'GET',
          params: {
            current: page.current,
            pageSize: page.pageSize,
            keyword: keyword.trim() || undefined,
            changeType: changeType || undefined,
          },
        },
      );
      const data = resp?.data;
      setRows(data?.list || []);
      setPage((prev) => ({
        ...prev,
        total: data?.total || 0,
        current: data?.current || prev.current,
        pageSize: data?.pageSize || prev.pageSize,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.current, page.pageSize, keyword, changeType]);

  const columns: ColumnsType<FinanceLogRow> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 80 },
      { title: '订单号', dataIndex: 'bizOrderNo', width: 260, render: (v) => v || '--' },
      {
        title: '类型',
        dataIndex: 'changeType',
        width: 120,
        render: (v: string) => (
          <Tag color={typeColor[v] || 'default'}>{typeOptions.find((i) => i.value === v)?.label || v}</Tag>
        ),
      },
      {
        title: '余额变动',
        dataIndex: 'pointsChange',
        width: 120,
        align: 'right',
        render: (v: number) => {
          const n = Number(v || 0);
          return <span style={{ color: n < 0 ? '#cf1322' : '#389e0d' }}>{n.toFixed(2)}</span>;
        },
      },
      {
        title: '变动前余额',
        dataIndex: 'balanceBefore',
        width: 130,
        align: 'right',
        render: (v: number) => Number(v || 0).toFixed(2),
      },
      {
        title: '变动后余额',
        dataIndex: 'balanceAfter',
        width: 130,
        align: 'right',
        render: (v: number) => Number(v || 0).toFixed(2),
      },
      {
        title: '发生时间',
        dataIndex: 'occurredAt',
        width: 170,
        render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '--'),
      },
      { title: '备注', dataIndex: 'remark', ellipsis: true, render: (v: string) => v || '--' },
    ],
    [],
  );

  return (
    <Card
      title="财务日志"
      styles={{ body: { paddingTop: 12 } }}
      extra={
        <Space>
          <Input
            style={{ width: 240 }}
            placeholder="订单号 / 备注"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setPage((p) => ({ ...p, current: 1 }));
            }}
            allowClear
          />
          <Select
            allowClear
            style={{ width: 160 }}
            placeholder="变动类型"
            options={typeOptions}
            value={changeType}
            onChange={(v) => {
              setChangeType(v);
              setPage((p) => ({ ...p, current: 1 }));
            }}
          />
          <Button
            onClick={() => {
              setKeyword('');
              setChangeType(undefined);
              setPage((p) => ({ ...p, current: 1 }));
            }}
          >
            重置
          </Button>
        </Space>
      }
    >
      <Table<FinanceLogRow>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page.current,
          pageSize: page.pageSize,
          total: page.total,
          showSizeChanger: true,
          onChange: (current, pageSize) => setPage((p) => ({ ...p, current, pageSize })),
        }}
      />
    </Card>
  );
};

export const FinanceLogsModalTrigger: React.FC<{
  balance: number;
}> = ({ balance }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tag
        className="qn-topnav-points"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true);
        }}
        style={{
          height: 34,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 14px',
          borderRadius: 999,
          border: '1px solid var(--app-border)',
          background: 'color-mix(in srgb, var(--app-primary) 10%, transparent)',
          color: 'var(--app-text)',
          fontWeight: 700,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'linear-gradient(135deg, color-mix(in srgb, var(--app-primary) 92%, #a855f7) 0%, color-mix(in srgb, var(--app-primary) 78%, #22d3ee) 100%)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          $
        </span>
        <span style={{ fontSize: 16, letterSpacing: 0.2 }}>{Number(balance || 0).toFixed(2)}</span>
        <span style={{ fontSize: 13, color: 'var(--app-text-secondary)', fontWeight: 600 }}>余额</span>
      </Tag>

      <Modal
        title={null}
        footer={null}
        open={open}
        onCancel={() => setOpen(false)}
        width={1200}
        destroyOnClose
        styles={{ body: { paddingTop: 12, overflowX: 'auto' } }}
      >
        <FinanceLogsTable />
      </Modal>
    </>
  );
};

